import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Button,
  useDisclosure,
  Spacer,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  BorrowingBaseReportGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableIdGroupWithLoanProductGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useGetVerificationBorrowingBaseWaiverImpactQuery } from '../requests/requests.gql.generated';
import { extractDataTapeMapFromRequiredDataTapes } from './extractDataTapeMapFromRequiredDataTapes';
import { uploadTapeForLenderVerification } from './uploadTapeForLenderVerification';
import {
  isVerificationBorrowingBaseWaiverImpactData,
  waiverImpactHeaderElementId,
} from './utils';
import { VerificationReceivableEligibilityUpdateWaiversDrawerContainer } from './VerificationReceivableEligibilityUpdateWaiversDrawer';
import { VerificationReportWaiverImpactConcentration } from './VerificationReportWaiverImpactConcentration';
import { VerificationReportWaiverImpactEligibility } from './VerificationReportWaiverImpactEligibility';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import {
  errorToastConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useGetRequiredDataTapesForVerificationQuery } from '~/packages/manualDataTapeUploads/requests/requests.gql.generated';
import { DataTape } from '~/packages/manualDataTapeUploads/types';

const WaiverImpactRecalculateButton = ({
  recalculate,
}: {
  recalculate: () => void;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  // const { isOpen, onOpen, onClose } = useDisclosure();
  const { navigator } = useVerificationOptions();

  const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
  const onRecalculate = useCallback(async () => {
    try {
      setIsRecalculating(true);

      await recalculate();

      navigator(navigate).reset();
      toast({
        ...successToastConfig,
        id: 'successRecalculateVerificationStart',
        title: 'Verification recalculation successfully started.',
      });
    } catch (err: any) {
      toast({
        ...errorToastConfig,
        id: 'errorRecalculateVerificationStart',
        title: 'Error starting recalculation.',
      });
    } finally {
      setIsRecalculating(false);
    }
  }, [recalculate, navigate, toast, navigator]);

  return (
    <Button
      colorScheme="aurora"
      onClick={() => onRecalculate()}
      isLoading={isRecalculating}
    >
      Recalculate
    </Button>
  );
};

const WaiverImpactHeadingText = () => {
  return (
    <Heading as="h4" fontSize="20px" id={waiverImpactHeaderElementId}>
      Waiver Impact
    </Heading>
  );
};

const WaiverImpactHeader = ({
  recalculate,
  creditFacilityId,
  reportingDate,
  loanProducts,
  receivableIdGroupsByLoanProduct,
  receivableWaiversByLoanProduct,
  borrowerOrganizationName,
}: {
  recalculate: () => void;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  loanProducts: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[];
  receivableIdGroupsByLoanProduct: {
    [loanProductId: string]: ReceivableIdGroupWithLoanProductGQLType[];
  };
  receivableWaiversByLoanProduct: {
    [loanProductId: string]: ReceivableBorrowingBaseTestWaiverGQLType[];
  };
  borrowerOrganizationName: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLoanProductId, setSelectedLoanProductId] = useState<string>(
    loanProducts[0].entity.id,
  );
  const [haveWaiversBeenUpdated, setHaveWaiversBeenUpdated] = useState<boolean>(
    false,
  );

  const onSelectLoanProduct = useCallback(
    (loanProductId: string) => {
      onOpen();
      setSelectedLoanProductId(loanProductId);
    },
    [onOpen],
  );

  const recalculateButtonTooltip =
    'Waivers have been updated. "Recalculate" to view an updated verification report. This process will take several minutes.';

  return (
    <Box mb="16px">
      <Flex alignItems="center" mb="16px">
        <WaiverImpactHeadingText />
        <Spacer />
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            backgroundColor="white"
            _active={{ backgroundColor: 'white' }}
          >
            Manage Waivers
          </MenuButton>
          <MenuList>
            {_.map(loanProducts, (loanProduct) => (
              <MenuItem
                onClick={() => onSelectLoanProduct(loanProduct.entity.id)}
                key={loanProduct.entity.id}
              >
                {loanProduct.entity.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {haveWaiversBeenUpdated && (
          <Box ml="32px">
            <Tooltip label={recalculateButtonTooltip}>
              <span>
                <WaiverImpactRecalculateButton recalculate={recalculate} />
              </span>
            </Tooltip>
          </Box>
        )}

        <VerificationReceivableEligibilityUpdateWaiversDrawerContainer
          isOpen={isOpen}
          onClose={onClose}
          creditFacilityId={creditFacilityId}
          loanProductId={selectedLoanProductId}
          reportingDate={reportingDate}
          setHaveWaiversBeenUpdated={setHaveWaiversBeenUpdated}
          loanProductReceivableIdGroups={
            receivableIdGroupsByLoanProduct[selectedLoanProductId] ?? []
          }
          receivableWaiversForLoanProduct={
            receivableWaiversByLoanProduct[selectedLoanProductId] ?? []
          }
          borrowerOrganizationName={borrowerOrganizationName}
        />
      </Flex>
      {haveWaiversBeenUpdated && (
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight={500}>
              Select &quot;Recalculate&quot; to view an updated verification
              report. This process will take several minutes.
            </Text>
          </AlertDescription>
        </Alert>
      )}
    </Box>
  );
};

type VerificationReportWaiverImpactProps = {
  verificationReportId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  calcAgentMechanics: CreditAgreement.DomainEntity.CalcAgentMechanicsWithEntities;
  borrowingBaseReport: BorrowingBaseReportGQLType;
  allReceivableWaivers?: ReceivableBorrowingBaseTestWaiverGQLType[]; // does not exist if view only mode
  borrowerOrganizationName: string;
};
export const VerificationReportWaiverImpact = ({
  verificationReportId,
  creditFacilityId,
  reportingDate,
  calcAgentMechanics,
  borrowingBaseReport,
  allReceivableWaivers,
  borrowerOrganizationName,
}: VerificationReportWaiverImpactProps) => {
  const { accessToken } = useUserAuthOrThrow();
  const toast = useToast();
  const [
    minimumReportingDate,
    setMinimumReportingDate,
  ] = useState<Util.FullDateStr | null>(null);
  const [dataTapes, setDataTapes] = useState<DataTape[]>([]);

  const {
    data: waiverImpactData,
    loading: isWaiverImpactLoading,
  } = useGetVerificationBorrowingBaseWaiverImpactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      verificationReportId,
      creditFacilityId,
    },
  });

  const {
    data: requiredDataTapesData,
  } = useGetRequiredDataTapesForVerificationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    onCompleted(data) {
      const {
        dataTapes: extractedDataTapes,
        reportingDate: reportingDateOfRequiredDataTapes,
      } = extractDataTapeMapFromRequiredDataTapes(data);

      setMinimumReportingDate(reportingDateOfRequiredDataTapes);
      setDataTapes(extractedDataTapes);
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (
    !minimumReportingDate ||
    !requiredDataTapesData ||
    !waiverImpactData ||
    isWaiverImpactLoading
  ) {
    return (
      <Box mt="40px">
        <WaiverImpactHeadingText />
        <Alert status="info" mt="24px">
          Calculating waiver impact...
        </Alert>
        <Progress size="xs" isIndeterminate />
      </Box>
    );
  }

  const upload = uploadTapeForLenderVerification({
    creditFacilityId,
    selectedReportingDate: minimumReportingDate,
    authToken: accessToken.raw,
    runInSandboxMode: false,
  });

  const recalculate = async () => {
    // re-upload previous files
    // here we pass file: null to indicate that
    // uploader should use previous file.
    await Promise.all(
      dataTapes.map((dataTape) =>
        upload({ artifactId: dataTape.artifactId, file: null }),
      ),
    );
  };

  const loanProducts = calcAgentMechanics.loanProductMechanics;
  const receivableWaiversByLoanProduct: {
    [loanProductId: string]: ReceivableBorrowingBaseTestWaiverGQLType[];
  } = _.groupBy(allReceivableWaivers, (waiver) => waiver.loanProductId);

  if (!waiverImpactData.verificationBorrowingBaseWaiverImpact) {
    return <></>;
  }

  const borrowingBaseWaiverImpact = JSON.parse(
    waiverImpactData.verificationBorrowingBaseWaiverImpact,
  );

  if (!isVerificationBorrowingBaseWaiverImpactData(borrowingBaseWaiverImpact)) {
    return <></>;
  }

  const receivableIdGroupsByLoanProduct: {
    [loanProductId: string]: ReceivableIdGroupWithLoanProductGQLType[];
  } = _.groupBy(
    borrowingBaseWaiverImpact.receivableResponseTapeReceivableIdGroups,
    (idGroup) => idGroup.loanProductId,
  );

  if (
    !_.size(borrowingBaseWaiverImpact.eligibilityWaiverImpact) &&
    !_.size(borrowingBaseWaiverImpact.concentrationWaiverImpact) &&
    allReceivableWaivers
  ) {
    return (
      <Box mt="40px" mb="80px">
        <WaiverImpactHeader
          recalculate={recalculate}
          creditFacilityId={creditFacilityId}
          reportingDate={reportingDate}
          loanProducts={loanProducts}
          receivableIdGroupsByLoanProduct={receivableIdGroupsByLoanProduct}
          receivableWaiversByLoanProduct={receivableWaiversByLoanProduct}
          borrowerOrganizationName={borrowerOrganizationName}
        />
        <Text>No impact from waivers.</Text>
      </Box>
    );
  }

  return (
    <Box mt="40px">
      {allReceivableWaivers && (
        <WaiverImpactHeader
          recalculate={recalculate}
          creditFacilityId={creditFacilityId}
          reportingDate={reportingDate}
          loanProducts={loanProducts}
          receivableIdGroupsByLoanProduct={receivableIdGroupsByLoanProduct}
          receivableWaiversByLoanProduct={receivableWaiversByLoanProduct}
          borrowerOrganizationName={borrowerOrganizationName}
        />
      )}
      <Box
        borderRadius="8px"
        border={`1px solid ${colors.gray[200]}`}
        backgroundColor="white"
        padding="24px"
      >
        <Tabs>
          <TabList>
            <Tab mt="0">Eligibility</Tab>
            <Tab mt="0">Concentration</Tab>
          </TabList>

          <TabPanels mt="8px">
            <TabPanel>
              <VerificationReportWaiverImpactEligibility
                eligibilityWaiverImpact={
                  borrowingBaseWaiverImpact.eligibilityWaiverImpact
                }
                receivableWaivers={allReceivableWaivers}
                reportingDate={reportingDate}
                borrowerOrganizationName={borrowerOrganizationName}
              />
            </TabPanel>
            <TabPanel>
              <VerificationReportWaiverImpactConcentration
                concentrationWaiverImpact={
                  borrowingBaseWaiverImpact.concentrationWaiverImpact
                }
                calcAgentMechanics={calcAgentMechanics}
                borrowingBaseReport={borrowingBaseReport}
                waiverImpact={borrowingBaseWaiverImpact}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};
